import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import { createHead } from '@unhead/vue'
import App from './App.vue'
import router from './router'
import store from './store'
import { restoreSessionCookie } from './utils/token-storage'

// Global styles
import '@fortawesome/fontawesome-free/css/all.css'
import 'animate.css'
import 'primevue/resources/themes/lara-light-blue/theme.css'
import 'primevue/resources/primevue.min.css'
import 'primeicons/primeicons.css'
import 'primeflex/primeflex.css'
// The lara theme is wrapped in `@layer primevue`; App.vue's unlayered
// `* { margin: 0; padding: 0 }` reset therefore beats every padding/margin the
// theme sets. These two files put the boxes back, unlayered, in DenvStack's
// metrics: dialogs first, then inputs/dropdowns/menus/tables/switches. Both
// load after the theme; see each file's header.
import './assets/dialog-inset.css'
import './assets/primevue-layer-restore.css'

// PrimeVue
//
// `app.use(PrimeVue)` installs the config/theme layer only — by design it
// registers no components and no directives. That is deliberate here, not an
// oversight: every PrimeVue control this app uses is registered LOCALLY in the
// SFC that uses it (`import Dialog from 'primevue/dialog'` + `components:
// { Dialog }`). tests/unit/component-registration.test.mjs enforces that, so an
// unregistered control is a failing test rather than a silent empty element.
//
// Do not "fix" this by globally registering the PrimeVue components here.
// Measured on this tree (vite build, 2026-09-13): every PrimeVue import lives
// behind a lazily-loaded authenticated route — the eight eagerly-imported
// public views (Home, Technical, Pricing, Contact, NotFound, ResetPassword,
// Activate, Docs) use none. Hoisting the 11 components + ripple/tooltip into
// this module pulls all of them into the entry chunk that every visitor
// downloads, including anonymous marketing traffic:
//
//     entry chunk   523.0 kB gzip  ->  617.8 kB gzip   (+94.8 kB, +18.1%)
//     total JS     3,181 kB raw    -> 3,188 kB raw     (unchanged; it only moves)
//     chunks              49       ->        43        (6 lazy chunks absorbed)
//
// i.e. ~95 kB of gzip on first paint bought nothing, because nothing was
// unresolved to begin with. If a future control genuinely needs to be global,
// register that one component here — the guard test honours app.component()
// and app.directive() registrations — rather than the whole library.
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'

// i18n
import translationEN from './locales/english.json'
import translationKA from './locales/georgian.json'

const messages = {
  en: translationEN,
  ka: translationKA
}

const i18n = createI18n({
  locale: 'en',
  fallbackLocale: 'en',
  messages
})

// Create Vue app
const app = createApp(App)

// Global properties
import { envConfig } from '@/utils/env-config'
import { formatVueError } from '@/utils/vue-error-codes'
import { reloadForChunkError } from '@/utils/chunk-reload'

// Stale-bundle self-heal (outside router navigation). Vite dispatches
// `vite:preloadError` when a dynamic import's CSS/JS preload fails — typically
// because a deploy replaced the hashed chunks this tab's shell still points
// at. The router hook covers navigation-triggered imports; this covers
// preloads that fail outside a navigation (e.g. a component-level lazy
// import). preventDefault() stops Vite re-throwing once we have decided to
// reload; otherwise we let it propagate so the failure still surfaces.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForChunkError(event, window.location.href)) {
    event.preventDefault()
  }
})
app.config.globalProperties.$api = {
  baseURL: envConfig.VUE_APP_API_FULL_URL || envConfig.VITE_API_FULL_URL
}

// Global error handler.
// `info` is words in a dev build but `https://vuejs.org/error-reference/#runtime-N`
// in a production one, which named nothing; `formatVueError` translates the
// code back and carries the stack, so a native failure like "Illegal
// invocation" arrives with the frame that made it.
app.config.errorHandler = (err, instance, info) => {
  const detail = formatVueError(err, instance, info)
  console.error('Global error:', detail.message, '\n  in:', detail.source,
    detail.component ? `\n  component: <${detail.component}>` : '')
  if (detail.stack) console.error(detail.stack)
  else console.error('Raw error:', err)

  // Send error to monitoring service in production
  if (import.meta.env.PROD) {
    // Add your error tracking service here
    // e.g., Sentry, LogRocket, etc.
  }
}

// Global warning handler
app.config.warnHandler = (msg, instance, trace) => {
  console.warn('Vue warning:', msg)
  console.warn('Trace:', trace)
}

// Performance monitoring
if (import.meta.env.PROD) {
  app.config.performance = true
}

// Security: Prevent Vue devtools in production
if (import.meta.env.PROD) {
  app.config.devtools = false
}

// Use plugins
app.use(store)
app.use(router)
app.use(i18n)
app.use(createHead())
app.use(PrimeVue)
app.use(ConfirmationService)
app.use(ToastService)

// Global directives
app.directive('click-outside', {
  beforeMount(el, binding) {
    el.clickOutsideEvent = function (event) {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event)
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el) {
    document.removeEventListener('click', el.clickOutsideEvent)
  }
})

// Initialize auth on app start, in this order:
//   1. reconcileSession — the store has just been rehydrated by vuex-persist;
//      refuse to carry on if that session and the token on disk are not the
//      same session (two sessions on one origin, see session-hygiene.js). It
//      runs FIRST so a mismatched token is never published as a cookie.
//   2. restoreSessionCookie — re-assert (or clear) the same-origin session
//      cookie from the stored token so the platform-dashboard pages see the
//      same session as the SPA — see src/utils/token-storage.js.
store.dispatch('auth/reconcileSession')
restoreSessionCookie()
store.dispatch('auth/initializeAuth')

// Global store reference for debugging (development only)
if (import.meta.env.DEV) {
  window.$store = store
}

// Navigation guards
router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isAuthenticated = store.getters['auth/isAuthenticated']

  if (requiresAuth && !isAuthenticated) {
    // Show auth modal instead of redirecting to login page
    store.commit('setShowAuthModal', true)
    next('/')
  } else {
    next()
  }
})

// Mount app
app.mount('#app')

// Security: Monitor authentication state
store.watch(
  (state) => state.auth.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      console.log('User authenticated - full access granted')
    } else {
      console.log('User not authenticated - restricted access')
    }
  }
)

// Performance monitoring
if ('performance' in window) {
  window.addEventListener('load', () => {
    setTimeout(() => {
      const perfData = performance.getEntriesByType('navigation')[0]
      console.log('Page load time:', perfData.loadEventEnd - perfData.loadEventStart, 'ms')
    }, 0)
  })
}

export default app
